import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Skeletons de carregamento com a forma do conteúdo (SZ-1589).
 *
 * Convenção: todo fallback de <Deferred> usa um destes blocos no lugar de
 * spinner/texto "Carregando...", imitando o layout real que está chegando
 * (linhas de tabela, cards, formulário). Larguras determinísticas — sem
 * aleatoriedade — para render estável entre re-renders.
 */

const LINE_WIDTHS = ['w-5/6', 'w-2/3', 'w-3/4', 'w-1/2', 'w-4/5', 'w-3/5'] as const

function lineWidth(index: number): string {
  return LINE_WIDTHS[index % LINE_WIDTHS.length]
}

// Texto acessível único por bloco; os retângulos são decorativos.
function SrCarregando() {
  return <span className="sr-only">Carregando…</span>
}

function CardShell({ children }: { children: ReactNode }) {
  return <div className="bg-card rounded-lg border border-border p-6">{children}</div>
}

/** Parágrafo: linhas de texto com larguras variadas. */
export function TextLinesSkeleton({ lines = 4 }: { lines?: number }) {
  return (
    <div role="status" className="space-y-2.5">
      <SrCarregando />
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-4 ${lineWidth(i)}`} />
      ))}
    </div>
  )
}

/** Tabela: linha de cabeçalho + linhas de dados em colunas. */
export function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div role="status" className="space-y-3">
      <SrCarregando />
      <div className="flex items-center gap-4 border-b border-border pb-2">
        <Skeleton className="h-3.5 w-1/3" />
        <Skeleton className="h-3.5 w-1/5" />
        <Skeleton className="ml-auto h-3.5 w-16" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className={`h-4 ${i % 2 === 0 ? 'w-2/5' : 'w-1/3'}`} />
          <Skeleton className="h-4 w-1/5" />
          <Skeleton className="ml-auto h-4 w-16" />
        </div>
      ))}
    </div>
  )
}

/** Lista: linhas com título + metadado, opcionalmente com avatar. */
export function ListRowsSkeleton({ rows = 5, avatar = false }: { rows?: number; avatar?: boolean }) {
  return (
    <div role="status" className="space-y-3">
      <SrCarregando />
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-start gap-3 py-1">
          {avatar && <Skeleton className="h-8 w-8 shrink-0 rounded-full" />}
          <div className="flex-1 space-y-1.5">
            <Skeleton className={`h-4 ${lineWidth(i)}`} />
            <Skeleton className="h-3 w-24" />
          </div>
        </div>
      ))}
    </div>
  )
}

/** Chips/badges: fileira de pílulas (ex.: contadores por provedor/status). */
export function ChipsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div role="status" className="flex flex-wrap gap-2">
      <SrCarregando />
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className={`h-6 rounded-full ${i % 3 === 0 ? 'w-24' : 'w-20'}`} />
      ))}
    </div>
  )
}

/** Grade de stat cards (KPIs): label curto + valor grande por card. */
export function StatCardsSkeleton({
  count = 6,
  gridClassName = 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
}: {
  count?: number
  gridClassName?: string
}) {
  return (
    <div role="status" className={`grid gap-3 ${gridClassName}`}>
      <SrCarregando />
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-lg border border-border bg-card p-4">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="mt-2 h-6 w-20" />
        </div>
      ))}
    </div>
  )
}

/** Card com título + tabela (ex.: rotinas, webhooks, evals). */
export function TableCardSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <CardShell>
      <Skeleton className="mb-5 h-5 w-44" />
      <TableSkeleton rows={rows} />
    </CardShell>
  )
}

/** Card com título + lista (ex.: auditoria, atividade recente). */
export function ListCardSkeleton({ rows = 5, avatar = false }: { rows?: number; avatar?: boolean }) {
  return (
    <CardShell>
      <Skeleton className="mb-5 h-5 w-44" />
      <ListRowsSkeleton rows={rows} avatar={avatar} />
    </CardShell>
  )
}

/** Card de formulário: pares label + campo e um botão (ex.: allowlist). */
export function FormCardSkeleton({ fields = 3 }: { fields?: number }) {
  return (
    <CardShell>
      <div role="status" className="space-y-5">
        <SrCarregando />
        <Skeleton className="h-5 w-44" />
        {Array.from({ length: fields }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
        <Skeleton className="h-9 w-32" />
      </div>
    </CardShell>
  )
}

/** Documento: cabeçalho com nome do arquivo + corpo de texto (ex.: memória, config). */
export function DocumentSkeleton({ lines = 6 }: { lines?: number }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2 border-b border-border pb-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-24" />
      </div>
      <TextLinesSkeleton lines={lines} />
    </div>
  )
}

/** Card de uso: título + descrição + barra de progresso + estatísticas. */
export function UsageCardSkeleton() {
  return (
    <CardShell>
      <div role="status" className="space-y-4">
        <SrCarregando />
        <div className="space-y-2">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-2.5 w-full rounded-full" />
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>
    </CardShell>
  )
}

/** Duas colunas: sidebar de arquivos + prévia de documento (ex.: memória). */
export function SplitPaneSkeleton() {
  return (
    <div className="flex min-h-96 overflow-hidden rounded-lg border border-border bg-card">
      <div role="status" className="hidden w-56 space-y-3 border-r border-border p-3 md:block">
        <SrCarregando />
        <Skeleton className="h-8 w-full" />
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className={`h-4 ${lineWidth(i)}`} />
        ))}
      </div>
      <div className="flex-1 p-4">
        <DocumentSkeleton lines={8} />
      </div>
    </div>
  )
}
